import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ImportColumnConfig } from '../../interfaces/import-config.interface';
import { ExcelGeneratorService } from '../../services/excel-generator.service';

export interface HeaderValidationErrorData {
  missingColumns: string[];
  warnings: string[];
  columns: ImportColumnConfig[];
  configTitle?: string;
}

@Component({
  selector: 'app-header-validation-error-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatSnackBarModule
  ],
  template: `
    <div mat-dialog-title class="flex items-center gap-3">
      <mat-icon class="text-red-600">error</mat-icon>
      <span>Formato de Excel Inválido</span>
    </div>

    <mat-dialog-content>
      <div class="space-y-4">
        <!-- Error Summary -->
        <div class="bg-red-50 border border-red-200 rounded-lg p-4">
          <div class="flex items-center gap-2 text-red-800 font-medium mb-2">
            <mat-icon class="w-5 h-5">warning</mat-icon>
            <span>El archivo no coincide con la plantilla esperada</span>
          </div>
          <p class="text-sm text-red-700">
            El archivo Excel que intentas importar no contiene todas las columnas requeridas.
            Por favor, verifica que estás usando la plantilla correcta.
          </p>
        </div>

        <!-- Missing Required Columns -->
        <div *ngIf="data.missingColumns.length > 0" class="border border-red-200 rounded-lg p-4 bg-white">
          <div class="flex items-center gap-2 text-red-800 font-medium mb-3">
            <mat-icon class="w-5 h-5">info</mat-icon>
            <span>Columnas requeridas faltantes ({{ data.missingColumns.length }})</span>
          </div>
          <div class="space-y-2">
            <div
              *ngFor="let column of data.missingColumns"
              class="flex items-center gap-2 text-sm">
              <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Requerida
              </span>
              <span class="text-gray-900 font-medium">{{ column }}</span>
            </div>
          </div>
        </div>

        <!-- Warnings for Optional Columns -->
        <div *ngIf="data.warnings.length > 0" class="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
          <div class="flex items-center gap-2 text-yellow-800 font-medium mb-3">
            <mat-icon class="w-5 h-5">info</mat-icon>
            <span>Columnas opcionales no encontradas ({{ data.warnings.length }})</span>
          </div>
          <div class="space-y-1">
            <p *ngFor="let warning of data.warnings" class="text-sm text-yellow-800">
              • {{ warning }}
            </p>
          </div>
        </div>

        <!-- Help Section -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div class="flex items-center gap-2 text-blue-800 font-medium mb-2">
            <mat-icon class="w-5 h-5">help_outline</mat-icon>
            <span>¿Cómo resolver esto?</span>
          </div>
          <div class="space-y-2 text-sm text-blue-800">
            <p>1. Descarga la plantilla correcta haciendo clic en el botón "Descargar Plantilla"</p>
            <p>2. Copia tus datos al archivo de plantilla descargado</p>
            <p>3. Asegúrate de que los nombres de las columnas coincidan exactamente</p>
            <p>4. Vuelve a intentar la importación con el archivo corregido</p>
          </div>
        </div>

        <!-- Expected Columns Info -->
        <div class="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div class="text-sm text-gray-700 mb-2 font-medium">
            Columnas esperadas en la plantilla:
          </div>
          <div class="flex flex-wrap gap-2">
            <span
              *ngFor="let column of data.columns"
              class="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
              [class.bg-red-100]="column.required"
              [class.text-red-800]="column.required"
              [class.bg-gray-200]="!column.required"
              [class.text-gray-700]="!column.required">
              {{ column.excelColumnName || column.displayName }}
              <span *ngIf="column.required" class="ml-1">*</span>
            </span>
          </div>
          <p class="text-xs text-gray-500 mt-2">* Indica columnas requeridas</p>
        </div>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions class="flex justify-between p-4">
      <button mat-button (click)="close()">
        <mat-icon>close</mat-icon>
        Cerrar
      </button>
      <button
        mat-raised-button
        color="primary"
        (click)="downloadTemplate()"
        [disabled]="isDownloading">
        <mat-icon>{{ isDownloading ? 'hourglass_empty' : 'download' }}</mat-icon>
        {{ isDownloading ? 'Descargando...' : 'Descargar Plantilla' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .header-validation-error-dialog {
      min-width: 500px;
      max-width: 600px;
    }

    .space-y-4 > * + * {
      margin-top: 1rem;
    }

    .space-y-3 > * + * {
      margin-top: 0.75rem;
    }

    .space-y-2 > * + * {
      margin-top: 0.5rem;
    }

    .space-y-1 > * + * {
      margin-top: 0.25rem;
    }

    mat-dialog-content {
      max-height: 70vh;
      overflow-y: auto;
    }
  `]
})
export class HeaderValidationErrorDialogComponent {
  isDownloading = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: HeaderValidationErrorData,
    private dialogRef: MatDialogRef<HeaderValidationErrorDialogComponent>,
    private excelGeneratorService: ExcelGeneratorService,
    private snackBar: MatSnackBar
  ) {}

  async downloadTemplate(): Promise<void> {
    try {
      this.isDownloading = true;

      // Generate template file name
      const configTitle = this.data.configTitle || 'import';
      const fileName = `plantilla_${configTitle.replace(/\s+/g, '_').toLowerCase()}_${new Date().getTime()}.xlsx`;

      // Generate Excel template
      const templateFile = await this.excelGeneratorService.generateTemplateFile(
        this.data.columns,
        fileName
      );

      // Download the file
      this.downloadFile(templateFile);

      // Show success message
      this.snackBar.open(
        'Plantilla descargada exitosamente. Usa este archivo para importar tus datos.',
        'Cerrar',
        {
          duration: 5000,
          panelClass: ['success-snackbar']
        }
      );

      // Close dialog after successful download
      setTimeout(() => {
        this.close();
      }, 1000);

    } catch (error) {
      this.snackBar.open(
        `Error al generar la plantilla: ${error instanceof Error ? error.message : 'Error desconocido'}`,
        'Cerrar',
        {
          duration: 5000,
          panelClass: ['error-snackbar']
        }
      );
    } finally {
      this.isDownloading = false;
    }
  }

  private downloadFile(file: File): void {
    const url = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  close(): void {
    this.dialogRef.close();
  }
}
